import { Paperclip } from 'lucide-react';

import {
  documentBodyFromMarkdown,
  documentTitleFromMarkdown,
  type DocumentDraft,
} from '@/entities/document-draft';
import { MemoizedMarkdown } from '@/shared/ui/primitives';

// The fax PDF keeps single newlines, so the header lines stay on separate
// lines; plain Markdown would merge them into one paragraph.
function withHardLineBreaks(markdown: string): string {
  return markdown.replace(/(?<!\n)\n(?!\n)/g, '  \n');
}

/** Shows the referral as the message Metriport will deliver: a subject and a body. */
export function ReferralMessagePreview({ draft }: { draft: DocumentDraft }) {
  const subject = documentTitleFromMarkdown(draft.content.bodyMarkdown);
  const body = documentBodyFromMarkdown(draft.content.bodyMarkdown);
  const attachments = draft.content.attachments;

  return (
    <div className="h-full overflow-auto px-4 py-6">
      <article
        className="mx-auto w-full max-w-2xl rounded-lg border bg-background"
        aria-label="Metriport message preview"
      >
        <div className="flex items-baseline gap-4 border-b px-8 py-5">
          <span className="w-14 shrink-0 text-xs text-muted-foreground">Subject</span>
          <h2 className={subject ? 'text-base font-medium' : 'text-base text-muted-foreground'}>
            {subject || 'No subject'}
          </h2>
        </div>

        <div className="px-8 py-6 text-sm leading-relaxed text-foreground/90 [&_h1]:mb-1 [&_h1]:mt-6 [&_h1]:text-sm [&_h1]:font-semibold [&_h1]:text-foreground [&_h2]:mb-1 [&_h2]:mt-6 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-foreground [&_h3]:mb-1 [&_h3]:mt-5 [&_h3]:text-sm [&_h3]:font-medium [&_p]:my-2 [&>*:first-child]:mt-0">
          {body === '' ? (
            <p className="text-muted-foreground">No message yet.</p>
          ) : (
            <MemoizedMarkdown id={draft.id} content={withHardLineBreaks(body)} />
          )}
        </div>

        {attachments.length > 0 ? (
          <ul className="space-y-1.5 border-t px-8 py-4" aria-label="Attachments">
            {attachments.map((attachment) => (
              <li
                key={attachment.attachmentId}
                className="flex items-center gap-2 text-xs text-muted-foreground"
              >
                <Paperclip className="size-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">{attachment.filename}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </article>
    </div>
  );
}
